import * as React from "react";
import { Grid, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Card } from "components/Card/Card.jsx";
import theme from "../styles/theme";
import { INSTANCES, getCurrentInstance } from "../config/instances";
import { getTopVisited } from "../config/visitTracker";
import { swaggerUrl } from "../config/apiUrl";

// Swagger is served by the backend of whichever instance served this bundle, so
// this always points at the API you're actually talking to.
const API_LINKS = [
  {
    label: "Management Portal Swagger UI",
    url: swaggerUrl,
  },
];

const GUIDES = [
  {
    label: "Management Portal Wiki",
    url: "https://dev.azure.com/repstor/Collaborate/_wiki/wikis/Collaborate%20Wiki/1727/Management-Portal",
  },
  {
    label: "Feature Guide",
    url: "https://dev.azure.com/repstor/Collaborate/_wiki/wikis/Collaborate%20Wiki/2422/Feature-guide",
  },
  {
    label: "Instances & Access",
    url: "https://dev.azure.com/repstor/Collaborate/_wiki/wikis/Collaborate%20Wiki/1752/Instances-Access",
  },
];

export default class Home extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentInstance: getCurrentInstance(),
    };
  }

  getFirstName() {
    const user = this.props.userAgentApplication?.getUser?.();
    const fullName = user?.name;
    return fullName ? fullName.split(" ")[0] : "there";
  }

  renderWelcome() {
    return (
      <Col xs={12}>
        <h3 className="ms-font-xxl" style={styles.welcomeHeading}>
          Welcome back, {this.getFirstName()}
        </h3>
      </Col>
    );
  }

  renderAliases(instance) {
    const aliases = instance.aliases || [];
    if (aliases.length === 0) {
      return null;
    }

    return (
      <div style={styles.aliasRow}>
        <div>
          {aliases.map((alias) => (
            <span key={alias} style={styles.alias}>
              {alias}
            </span>
          ))}
        </div>
        {instance.aliasNote && (
          <div className="ms-font-s text-muted" style={styles.aliasNote}>
            {instance.aliasNote}
          </div>
        )}
      </div>
    );
  }

  renderInstances() {
    const { currentInstance } = this.state;
    const envColor =
      currentInstance.env === "prod"
        ? theme.colors.warning
        : currentInstance.env === "nonprod"
          ? theme.colors.success
          : theme.colors.primary;
    return (
      <Col xs={12}>
        <Card
          title="Instances"
          category="Jump to another Management Portal deployment"
          content={
            <Row>
              {INSTANCES.map((instance) => {
                const isCurrent = instance.id === currentInstance.id;
                const body = (
                  <React.Fragment>
                    <div style={styles.tileHeader}>
                      <div>
                        <div className="ms-font-xl">
                          <b>{instance.label}</b>
                        </div>
                        <div className="ms-font-s text-muted">
                          {instance.subscription} &middot;{" "}
                          {instance.env === "prod"
                            ? "Production"
                            : "Non-production"}
                        </div>
                      </div>
                      {this.renderAliases(instance)}
                    </div>
                  </React.Fragment>
                );
                return (
                  <Col md={4} sm={6} key={instance.id}>
                    {isCurrent ? (
                      <div
                        style={{
                          ...styles.instanceTile,
                          ...styles.instanceTileCurrent,
                          borderColor: envColor,
                        }}
                      >
                        {body}
                        <div style={styles.badgeWrapper}>
                          <span
                            style={{
                              ...styles.badge,
                              backgroundColor: envColor,
                            }}
                          >
                            You are here
                          </span>
                        </div>
                      </div>
                    ) : (
                      <a
                        href={instance.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          ...styles.instanceTile,
                          ...styles.instanceTileLink,
                        }}
                      >
                        {body}
                        <div style={styles.badgeWrapperHidden}>
                          <span style={styles.badge}>You are here</span>
                        </div>
                      </a>
                    )}
                  </Col>
                );
              })}
            </Row>
          }
        />
      </Col>
    );
  }

  renderLinkList(links) {
    return (
      <ul className="list-unstyled" style={styles.linkList}>
        {links.map((link) => (
          <li key={link.url} style={styles.linkListItem}>
            <a href={link.url} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>{" "}
            <span className="text-muted">&#8599;</span>
          </li>
        ))}
      </ul>
    );
  }

  renderGuides() {
    return (
      <Col xs={12}>
        <Card
          title="Useful Links"
          category="API reference and wiki documentation"
          content={
            <React.Fragment>
              <div style={styles.linkSection}>
                <div className="ms-font-l" style={styles.linkSectionHeading}>
                  API
                </div>
                {this.renderLinkList(API_LINKS)}
              </div>
              <div>
                <div className="ms-font-l" style={styles.linkSectionHeading}>
                  Wiki
                </div>
                {this.renderLinkList(GUIDES)}
              </div>
            </React.Fragment>
          }
        />
      </Col>
    );
  }

  renderMostVisited() {
    const routes = this.props.routes || [];
    const items = getTopVisited(4)
      .map((visit) =>
        routes.find((route) => route.layout + route.path === visit.path),
      )
      .filter((route) => route && route.path !== "/home");

    return (
      <Col xs={12}>
        <Card
          title="Most visited"
          category="Your most-used tools"
          content={
            items.length === 0 ? (
              <p className="ms-font-m text-muted" style={styles.emptyState}>
                Your most-used tools will appear here as you navigate.
              </p>
            ) : (
              <ul className="list-unstyled" style={styles.linkList}>
                {items.map((route) => (
                  <li key={route.path} style={styles.linkListItem}>
                    <Link
                      to={route.layout + route.path}
                      style={styles.visitedLink}
                    >
                      {route.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )
          }
        />
      </Col>
    );
  }

  render() {
    return (
      <div style={styles.container}>
        <Grid fluid>
          <Row>{this.renderWelcome()}</Row>
          <Row>{this.renderInstances()}</Row>
          <Row>{this.renderGuides()}</Row>
          <Row>{this.renderMostVisited()}</Row>
        </Grid>
      </div>
    );
  }
}

const styles = {
  container: {
    marginTop: "20px",
  },
  welcomeHeading: {
    marginTop: 0,
  },
  instanceTile: {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "6px",
    padding: "16px",
    marginBottom: "10px",
    backgroundColor: theme.colors.cardBackground,
  },
  instanceTileLink: {
    display: "block",
    textDecoration: "none",
    color: theme.colors.primary,
  },
  instanceTileCurrent: {
    borderColor: theme.colors.primary,
  },
  tileHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  aliasRow: {
    textAlign: "right",
    marginLeft: "12px",
    flexShrink: 0,
  },
  alias: {
    display: "inline-block",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "3px",
    padding: "1px 8px",
    marginLeft: "6px",
    // Set explicitly: these tiles are wrapped in an <a>, which would otherwise tint the text
    color: theme.colors.text,
    fontSize: theme.typography.fontSize.small,
  },
  aliasNote: {
    marginTop: "4px",
  },
  badgeWrapper: {
    marginTop: "12px",
  },
  badgeWrapperHidden: {
    marginTop: "12px",
    visibility: "hidden",
  },
  badge: {
    backgroundColor: theme.colors.primary,
    color: "#fff",
    padding: "3px 10px",
    borderRadius: "3px",
    fontSize: theme.typography.fontSize.small,
  },
  linkList: {
    marginBottom: 0,
  },
  linkListItem: {
    marginBottom: "10px",
  },
  linkSection: {
    marginBottom: "24px",
  },
  linkSectionHeading: {
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: "2px",
  },
  emptyState: {
    marginBottom: 0,
  },
  visitedLink: {
    display: "inline-flex",
    alignItems: "center",
  },
};
